import React from 'react';

import cx from 'classnames';
import { useCompleteModal } from 'modal.react.js';
import { isNotNil } from 'ramda';
import { FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { AModal } from 'components/shared/AModal';
import { IAModalProps } from 'components/shared/AModal/types';
import Icon from 'components/shared/Icon';
import ArrowLeft from 'components/shared/Icon/icons/ArrowLeft';
import Text from 'components/shared/Typography/Text';
import { ETripEvent, ITripEvent } from 'detailsv2/structure';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';

import { EPageType } from '../TripEventModalContents/Area/types';
import { BackButtonWithTooltip } from '../TripEventModalContents/shared';
import { DetailsScene } from '../TripEventModalContents/shared/DetailsScene';
import sharedStyles from '../TripEventModalContents/shared/index.module.css';
import { PageScene } from '../TripEventModalContents/shared/PageScene';
import { AccommodationButtonGroup } from './AccommodationButtonGroup';
import { AccommodationForm as AccommodationBaseForm } from './AccomodationForm';
import { useAccomodationForm } from './AccomodationForm/hooks';
import { withWizardContext, useAccomodationWizardContext } from './context';
import { suggestedAreaFormResolver } from './functions';
import s from './index.module.css';
import { SuggestedAreaForm } from './SuggestedAreaForm';
import { TFormValues } from './SuggestedAreaForm/types';

const AccommodationForm = function () {
  const { methods, onCreateOption } = useAccomodationForm();

  return (
    <FormProvider {...methods}>
      <AccommodationBaseForm onCreateOption={onCreateOption} />
      <AccommodationButtonGroup />
    </FormProvider>
  );
};

const ModalHeader = React.memo(() => {
  const { step, tripEvent, goToStep } = useAccomodationWizardContext();

  const isEditing = isNotNil(tripEvent.id);

  if (step === 'suggested-area') {
    return (
      <>
        <Icon component={ArrowLeft} className={s.icon} onClick={() => goToStep('default')} />

        <div className={s['centered-title']}>
          <Text>Add Area Page</Text>
        </div>
      </>
    );
  }

  if (isEditing) return <Text>Edit Accommodation to Your Itinerary</Text>;
  return <Text>Add Accommodation to Your Itinerary</Text>;
});

export const AccomodationModal = withWizardContext(() => {
  const dispatch = useDispatch();
  const contextData = useAccomodationWizardContext();
  const {
    step,
    updateData,
    goToStep,
    previousStep,
    submitData,
    data: { default: accommodation },
    detailsFormProps,
  } = contextData;
  const modalData = { ...contextData, initialValues: contextData.data.default };

  const isPageHasChanges = useSelector(appHasChangesSelector);
  const isChangesWarning = isPageHasChanges || detailsFormProps.hasChanges || detailsFormProps.isFilled;
  const organizedBy = useSelector(itineraryScheduleTypeSelector);

  const completeModal = useCompleteModal<ITripEvent<ETripEvent.Accommodation>[] | false>();

  const handleSubmitSuggestedArea = (values: TFormValues) => {
    const submitAreaValue = suggestedAreaFormResolver(values, { accommodation, organizedBy });
    updateData('suggested-area', submitAreaValue);
    submitData();
  };

  const updateDefaultData = () => {
    if (accommodation) {
      const updatedPages = accommodation?.attachedPages?.map((page) => ({ ...page, isNew: true }));
      const updatedData = { ...accommodation, attachedPages: updatedPages };
      updateData('default', updatedData as any); // TODO: fix type
    }
  };

  const handleNavToPreviousStep = () => {
    updateDefaultData();
    previousStep();
  };

  const handleNavToDefaultStep = () => {
    updateDefaultData();
    goToStep('default');
  };

  const onReturn = (() => {
    if (step === 'default') return undefined;

    if (step === 'suggested-area') return handleNavToDefaultStep;
    return handleNavToPreviousStep;
  })();

  const modalProps: Pick<
    IAModalProps,
    'header' | 'headerPlacement' | 'withoutCloseBtn' | 'onReturn' | 'size' | 'withoutPadding'
  > = {
    header: step === 'default' || step === 'suggested-area' ? <ModalHeader /> : 'Create New Content',
    headerPlacement: step === 'default' ? undefined : 'center',
    withoutCloseBtn: step === 'default' ? false : true,
    withoutPadding: step === 'page' || step === 'details',
    size: step === 'page' ? 'xl' : 'xlg',
    onReturn,
  };

  const handleBackButtonOnClick = () => {
    updateDefaultData();

    if (step === 'suggested-area') return goToStep('default');

    previousStep();
    dispatch(setAppHasChangesAction(false));
  };

  const customBackButton = (() => {
    if (!onReturn) return null;
    if (!isChangesWarning) return null;
    return <BackButtonWithTooltip onClick={handleBackButtonOnClick} />;
  })();

  return (
    <AModal
      headerClassName={s.header}
      isOpen
      onClose={() => completeModal.success(false)}
      bodyClassName={cx(sharedStyles.bodyModal)}
      customBackButton={customBackButton}
      isCloseOnEsc
      {...modalProps}
    >
      {step === 'default' && <AccommodationForm />}
      {step === 'details' && <DetailsScene modalData={modalData} type="secondary" initialType={EPageType.PROPERTY} />}
      {step === 'page' && (
        <PageScene modalData={modalData} withAIActions={detailsFormProps.type === EPageType.PROPERTY} />
      )}
      {step === 'suggested-area' && <SuggestedAreaForm onSubmit={handleSubmitSuggestedArea} />}
    </AModal>
  );
});
